import { Connection, PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { getCurrentRpcEndpoint, SOLANA_CONFIG } from '../config/security';
import { secureStorage, sanitizeErrorMessage } from '../utils/security';

/**
 * Proof of Humanity Service for Anti-Bot Protection
 * Implements multiple verification methods to ensure human users
 */
export class ProofOfHumanityService {
  constructor(connection, wallet) {
    this.connection = connection || new Connection(getCurrentRpcEndpoint());
    this.wallet = wallet;
    
    // Verification methods and their weights
    this.VERIFICATION_METHODS = {
      CAPTCHA: { weight: 10, duration: 24 * 60 * 60 * 1000 }, // 24 hours
      PHONE_VERIFICATION: { weight: 30, duration: 30 * 24 * 60 * 60 * 1000 }, // 30 days
      EMAIL_VERIFICATION: { weight: 20, duration: 7 * 24 * 60 * 60 * 1000 }, // 7 days
      SOCIAL_MEDIA_LINK: { weight: 25, duration: 30 * 24 * 60 * 60 * 1000 }, // 30 days
      VIDEO_SELFIE: { weight: 50, duration: 90 * 24 * 60 * 60 * 1000 }, // 90 days
      GOVERNMENT_ID: { weight: 100, duration: 365 * 24 * 60 * 60 * 1000 }, // 1 year
      BIOMETRIC_SCAN: { weight: 80, duration: 180 * 24 * 60 * 60 * 1000 }, // 180 days
      BEHAVIORAL_ANALYSIS: { weight: 40, duration: 7 * 24 * 60 * 60 * 1000 }, // 7 days
      NETWORK_ANALYSIS: { weight: 15, duration: 24 * 60 * 60 * 1000 }, // 24 hours
      DEVICE_FINGERPRINT: { weight: 20, duration: 30 * 24 * 60 * 60 * 1000 } // 30 days
    };

    // Humanity score thresholds
    this.HUMANITY_THRESHOLDS = {
      SUSPICIOUS: 0,      // Likely bot
      UNVERIFIED: 30,     // Unverified human
      BASIC_HUMAN: 60,    // Basic human verification
      VERIFIED_HUMAN: 100, // Verified human
      TRUSTED_HUMAN: 150   // Highly trusted human
    };

    // Bot detection patterns
    this.BOT_PATTERNS = {
      RAPID_ACTIONS: { threshold: 10, timeWindow: 60000 }, // 10 actions per minute
      IDENTICAL_CONTENT: { threshold: 0.95 }, // 95% similarity
      SUSPICIOUS_TIMING: { threshold: 100 }, // Actions every 100ms
      NETWORK_CLUSTERING: { threshold: 5 }, // 5+ accounts from same IP
      BEHAVIORAL_ANOMALIES: { threshold: 0.8 } // 80% anomaly score
    };
  }

  /**
   * Get user's current humanity score
   */
  async getHumanityScore(walletAddress) {
    try {
      const cacheKey = `humanity_${walletAddress}`;
      const cached = secureStorage.getItem(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < 300000) { // 5 minute cache
        return cached.score;
      }

      // Calculate humanity score from verifications
      const verifications = await this.getUserVerifications(walletAddress);
      const behaviorScore = await this.analyzeBehavior(walletAddress);
      const networkScore = await this.analyzeNetwork(walletAddress);
      
      let totalScore = 0;
      const activeVerifications = [];

      // Calculate score from active verifications
      for (const verification of verifications) {
        if (this.isVerificationActive(verification)) {
          totalScore += this.VERIFICATION_METHODS[verification.method].weight;
          activeVerifications.push(verification);
        }
      }

      // Add behavior and network analysis scores
      totalScore += behaviorScore;
      totalScore += networkScore;

      // Determine humanity level
      const humanityLevel = this.getHumanityLevel(totalScore);

      const result = {
        score: totalScore,
        level: humanityLevel,
        activeVerifications,
        behaviorScore,
        networkScore,
        isBot: totalScore < this.HUMANITY_THRESHOLDS.UNVERIFIED,
        lastUpdated: Date.now()
      };

      // Cache result
      secureStorage.setItem(cacheKey, {
        score: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('Error getting humanity score:', sanitizeErrorMessage(error));
      return {
        score: 0,
        level: 'SUSPICIOUS',
        error: true
      };
    }
  }

  /**
   * Get user's verification history
   */
  async getUserVerifications(walletAddress) {
    try {
      const verifications = secureStorage.getItem(`verifications_${walletAddress}`) || [];
      
      // Also check blockchain for verification records
      const blockchainVerifications = await this.getBlockchainVerifications(walletAddress);
      
      // Merge and deduplicate
      const allVerifications = [...verifications, ...blockchainVerifications];
      const uniqueVerifications = this.deduplicateVerifications(allVerifications);
      
      return uniqueVerifications;
    } catch (error) {
      console.error('Error getting user verifications:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Get verification records from blockchain
   */
  async getBlockchainVerifications(walletAddress) {
    try {
      const signatures = await this.connection.getSignaturesForAddress(
        new PublicKey(walletAddress),
        { limit: 500 }
      );

      const verifications = [];

      for (const sig of signatures) {
        try {
          const tx = await this.connection.getTransaction(sig.signature, {
            commitment: 'confirmed'
          });

          if (tx?.meta?.logMessages) {
            for (const log of tx.meta.logMessages) {
              if (log.includes('ChirpX Humanity:')) {
                const verificationData = this.extractVerificationFromLog(log);
                if (verificationData) {
                  verifications.push(verificationData);
                }
              }
            }
          }
        } catch (error) {
          // Skip failed transactions
          continue;
        }
      }

      return verifications;
    } catch (error) {
      console.error('Error getting blockchain verifications:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Extract verification data from transaction log
   */
  extractVerificationFromLog(log) {
    try {
      const match = log.match(/ChirpX Humanity:\s*(.+)/);
      if (match && match[1]) {
        return JSON.parse(match[1]);
      }
    } catch (error) {
      // Invalid JSON, skip
    }
    return null;
  }

  /**
   * Deduplicate verification records
   */
  deduplicateVerifications(verifications) {
    const seen = new Set();
    return verifications.filter(verification => {
      const key = `${verification.method}_${verification.identifier || verification.timestamp}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * Check if verification is still active
   */
  isVerificationActive(verification) {
    const method = this.VERIFICATION_METHODS[verification.method];
    if (!method) return false;
    
    const expiryTime = verification.timestamp + method.duration;
    return Date.now() < expiryTime;
  }

  /**
   * Get humanity level based on score
   */
  getHumanityLevel(score) {
    if (score >= this.HUMANITY_THRESHOLDS.TRUSTED_HUMAN) return 'TRUSTED_HUMAN';
    if (score >= this.HUMANITY_THRESHOLDS.VERIFIED_HUMAN) return 'VERIFIED_HUMAN';
    if (score >= this.HUMANITY_THRESHOLDS.BASIC_HUMAN) return 'BASIC_HUMAN';
    if (score >= this.HUMANITY_THRESHOLDS.UNVERIFIED) return 'UNVERIFIED';
    return 'SUSPICIOUS';
  }

  /**
   * Analyze user behavior for bot detection
   */
  async analyzeBehavior(walletAddress) {
    try {
      const recentActions = await this.getRecentActions(walletAddress);
      let behaviorScore = 0;
      const anomalies = [];

      // Check for rapid actions
      const rapidActions = this.detectRapidActions(recentActions);
      if (rapidActions.detected) {
        anomalies.push('RAPID_ACTIONS');
        behaviorScore -= 20;
      } else {
        behaviorScore += 10; // Normal action timing
      }

      // Check for identical content
      const identicalContent = this.detectIdenticalContent(recentActions);
      if (identicalContent.detected) {
        anomalies.push('IDENTICAL_CONTENT');
        behaviorScore -= 30;
      } else {
        behaviorScore += 15; // Diverse content
      }

      // Check for suspicious timing patterns
      const suspiciousTiming = this.detectSuspiciousTiming(recentActions);
      if (suspiciousTiming.detected) {
        anomalies.push('SUSPICIOUS_TIMING');
        behaviorScore -= 25;
      } else {
        behaviorScore += 10; // Natural timing
      }

      // Human-like behavior patterns
      const humanPatterns = this.detectHumanPatterns(recentActions);
      behaviorScore += humanPatterns.score;

      return Math.max(0, Math.min(40, behaviorScore)); // Cap at 40 points
    } catch (error) {
      console.error('Error analyzing behavior:', sanitizeErrorMessage(error));
      return 0;
    }
  }

  /**
   * Get recent user actions for analysis
   */
  async getRecentActions(walletAddress) {
    try {
      // Get recent transactions
      const signatures = await this.connection.getSignaturesForAddress(
        new PublicKey(walletAddress),
        { limit: 100 }
      );

      const actions = [];
      const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // Last 24 hours

      for (const sig of signatures) {
        if (sig.blockTime * 1000 < cutoffTime) break;

        try {
          const tx = await this.connection.getTransaction(sig.signature, {
            commitment: 'confirmed'
          });

          if (tx?.meta?.logMessages) {
            for (const log of tx.meta.logMessages) {
              if (log.includes('ChirpX') || log.includes('Znoospace')) {
                actions.push({
                  signature: sig.signature,
                  timestamp: sig.blockTime * 1000,
                  content: log,
                  type: this.classifyAction(log)
                });
              }
            }
          }
        } catch (error) {
          continue;
        }
      }

      return actions.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error('Error getting recent actions:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Classify action type from log
   */
  classifyAction(log) {
    if (log.includes('Beam:') || log.includes('Post:')) return 'POST';
    if (log.includes('Vote:')) return 'VOTE';
    if (log.includes('Flag:')) return 'FLAG';
    if (log.includes('DAO')) return 'DAO_ACTION';
    return 'OTHER';
  }

  /**
   * Detect rapid actions (bot-like behavior)
   */
  detectRapidActions(actions) {
    const timeWindow = this.BOT_PATTERNS.RAPID_ACTIONS.timeWindow;
    const threshold = this.BOT_PATTERNS.RAPID_ACTIONS.threshold;
    
    let maxActionsInWindow = 0;
    
    for (let i = 0; i < actions.length; i++) {
      const windowStart = actions[i].timestamp;
      const windowEnd = windowStart + timeWindow;
      
      let actionsInWindow = 0;
      for (let j = i; j < actions.length && actions[j].timestamp <= windowEnd; j++) {
        actionsInWindow++;
      }
      
      maxActionsInWindow = Math.max(maxActionsInWindow, actionsInWindow);
    }
    
    return {
      detected: maxActionsInWindow > threshold,
      maxActionsInWindow,
      threshold
    };
  }

  /**
   * Detect identical content (spam behavior)
   */
  detectIdenticalContent(actions) {
    const posts = actions.filter(action => action.type === 'POST');
    if (posts.length < 2) return { detected: false };
    
    let maxSimilarity = 0;
    
    for (let i = 0; i < posts.length - 1; i++) {
      for (let j = i + 1; j < posts.length; j++) {
        const similarity = this.calculateContentSimilarity(posts[i].content, posts[j].content);
        maxSimilarity = Math.max(maxSimilarity, similarity);
      }
    }
    
    return {
      detected: maxSimilarity > this.BOT_PATTERNS.IDENTICAL_CONTENT.threshold,
      maxSimilarity
    };
  }

  /**
   * Calculate content similarity
   */
  calculateContentSimilarity(content1, content2) {
    // Simple similarity calculation (could be enhanced with more sophisticated algorithms)
    const words1 = content1.toLowerCase().split(/\s+/);
    const words2 = content2.toLowerCase().split(/\s+/);
    
    const commonWords = words1.filter(word => words2.includes(word));
    const totalWords = new Set([...words1, ...words2]).size;
    
    return commonWords.length / totalWords;
  }

  /**
   * Detect suspicious timing patterns
   */
  detectSuspiciousTiming(actions) {
    if (actions.length < 3) return { detected: false };
    
    const intervals = [];
    for (let i = 0; i < actions.length - 1; i++) {
      intervals.push(actions[i].timestamp - actions[i + 1].timestamp);
    }
    
    // Check for too-regular intervals (bot-like)
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.reduce((sum, interval) => sum + Math.pow(interval - avgInterval, 2), 0) / intervals.length;
    const standardDeviation = Math.sqrt(variance);
    
    // Low variance indicates regular, bot-like timing
    const coefficientOfVariation = standardDeviation / avgInterval;
    
    return {
      detected: coefficientOfVariation < 0.2 && avgInterval < 10000, // Very regular and fast
      coefficientOfVariation,
      avgInterval
    };
  }

  /**
   * Detect human-like behavior patterns
   */
  detectHumanPatterns(actions) {
    let score = 0;
    
    // Diverse action types
    const actionTypes = new Set(actions.map(action => action.type));
    score += Math.min(actionTypes.size * 3, 15);
    
    // Natural timing variations
    if (actions.length > 1) {
      const intervals = [];
      for (let i = 0; i < actions.length - 1; i++) {
        intervals.push(actions[i].timestamp - actions[i + 1].timestamp);
      }
      
      const hasVariation = intervals.some(interval => interval > 60000); // Some gaps > 1 minute
      if (hasVariation) score += 10;
    }
    
    // Content variety (for posts)
    const posts = actions.filter(action => action.type === 'POST');
    if (posts.length > 1) {
      const avgSimilarity = this.calculateAverageContentSimilarity(posts);
      if (avgSimilarity < 0.5) score += 10; // Diverse content
    }
    
    return { score: Math.min(score, 25) };
  }

  /**
   * Calculate average content similarity across posts
   */
  calculateAverageContentSimilarity(posts) {
    if (posts.length < 2) return 0;
    
    let totalSimilarity = 0;
    let comparisons = 0;
    
    for (let i = 0; i < posts.length - 1; i++) {
      for (let j = i + 1; j < posts.length; j++) {
        totalSimilarity += this.calculateContentSimilarity(posts[i].content, posts[j].content);
        comparisons++;
      }
    }
    
    return comparisons > 0 ? totalSimilarity / comparisons : 0;
  }

  /**
   * Analyze network patterns for bot detection
   */
  async analyzeNetwork(walletAddress) {
    try {
      // This would typically involve analyzing IP addresses, device fingerprints, etc.
      // For now, return a neutral score
      return 15; // Neutral network score
    } catch (error) {
      console.error('Error analyzing network:', sanitizeErrorMessage(error));
      return 0;
    }
  }

  /**
   * Submit verification for a method
   */
  async submitVerification(method, data) {
    try {
      if (!this.wallet) {
        throw new Error('Wallet not connected');
      }

      if (!this.VERIFICATION_METHODS[method]) {
        throw new Error('Invalid verification method');
      }

      const verificationData = {
        method,
        walletAddress: this.wallet.publicKey.toString(),
        data: data,
        timestamp: Date.now(),
        status: 'PENDING',
        verificationId: `verify_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
      };

      // Store verification locally
      const userVerifications = secureStorage.getItem(`verifications_${this.wallet.publicKey.toString()}`) || [];
      userVerifications.push(verificationData);
      secureStorage.setItem(`verifications_${this.wallet.publicKey.toString()}`, userVerifications);

      // Create blockchain record
      const transaction = new Transaction();
      
      const verificationInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(`ChirpX Humanity: ${JSON.stringify({
          method,
          verificationId: verificationData.verificationId,
          timestamp: verificationData.timestamp,
          status: 'SUBMITTED'
        })}`, 'utf8')
      });

      transaction.add(verificationInstruction);

      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');

      return {
        success: true,
        verificationId: verificationData.verificationId,
        signature,
        method,
        status: 'PENDING',
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error submitting verification:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Check if user is likely a bot
   */
  async isBotLikely(walletAddress) {
    const humanityScore = await this.getHumanityScore(walletAddress);
    return humanityScore.isBot;
  }

  /**
   * Get verification requirements for user
   */
  getVerificationRequirements(currentScore) {
    const requirements = [];
    
    if (currentScore < this.HUMANITY_THRESHOLDS.BASIC_HUMAN) {
      requirements.push({
        method: 'CAPTCHA',
        points: this.VERIFICATION_METHODS.CAPTCHA.weight,
        description: 'Complete CAPTCHA verification',
        difficulty: 'Easy'
      });
      requirements.push({
        method: 'EMAIL_VERIFICATION',
        points: this.VERIFICATION_METHODS.EMAIL_VERIFICATION.weight,
        description: 'Verify email address',
        difficulty: 'Easy'
      });
    }
    
    if (currentScore < this.HUMANITY_THRESHOLDS.VERIFIED_HUMAN) {
      requirements.push({
        method: 'PHONE_VERIFICATION',
        points: this.VERIFICATION_METHODS.PHONE_VERIFICATION.weight,
        description: 'Verify phone number',
        difficulty: 'Medium'
      });
      requirements.push({
        method: 'SOCIAL_MEDIA_LINK',
        points: this.VERIFICATION_METHODS.SOCIAL_MEDIA_LINK.weight,
        description: 'Link social media account',
        difficulty: 'Medium'
      });
    }
    
    if (currentScore < this.HUMANITY_THRESHOLDS.TRUSTED_HUMAN) {
      requirements.push({
        method: 'VIDEO_SELFIE',
        points: this.VERIFICATION_METHODS.VIDEO_SELFIE.weight,
        description: 'Submit video selfie verification',
        difficulty: 'Hard'
      });
    }
    
    return requirements;
  }
}

export default ProofOfHumanityService;
